/**
 * chatApi.js — Komunikimi: klienti web mbi `backend/routes/chatRoutes.js`.
 *
 * I njëjti API që përdor edhe Nexuma Mobile (`nexuma-mobile/src/api.js`), ndaj
 * një mesazh i shkruar në telefon shfaqet këtu dhe anasjelltas — një bazë, një
 * grup tabelash (`chat_channels` / `chat_members` / `chat_messages`), dy klientë.
 *
 * Backend-i s'ka WebSocket (as `ws` as `socket.io`), ndaj freskimi bëhet me
 * `chatPoll`, i cili kthen VETËM mesazhet e ardhura pas një çasti të dhënë —
 * një kërkesë e lirë kur s'ka gjë të re. Kalimi te WebSocket-i më vonë s'e
 * ndryshon këtë modul, vetëm transportin brenda tij.
 *
 * Çdo funksion hedh `Error` me mesazhin e serverit kur përgjigja s'është 2xx,
 * që ekrani të tregojë shkakun e vërtetë (p.sh. "Nuk je anëtar i këtij kanali")
 * dhe jo një "gabim" gjenerik.
 */
import { authFetch } from './apiServices';

async function call(path, options) {
  let res;
  try {
    res = await authFetch(`/api/chat${path}`, options);
  } catch {
    throw new Error('Nuk u lidh dot me serverin.');
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    throw new Error(json?.error || `Gabim (HTTP ${res.status}).`);
  }
  return json?.data;
}

const post = (path, body) => call(path, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

/** { channels: [...], dms: [...], unread } — kanalet e mia dhe të palexuarat. */
export async function chatOverview() {
  return (await call('/overview')) || { channels: [], dms: [], unread: 0 };
}

/** { channel, messages } — mesazhet vijnë në rend kohor rritës. */
export async function chatMessages(channelId, { before, limit } = {}) {
  const qs = new URLSearchParams();
  if (before) qs.set('before', String(before));
  if (limit)  qs.set('limit', String(limit));
  const q = qs.toString();
  return (await call(`/channels/${channelId}/messages${q ? `?${q}` : ''}`)) || { channel: null, messages: [] };
}

/** `replyTo` është id e mesazhit që po i përgjigjemi — opsionale. */
export const chatSend = (channelId, body, replyTo = null) =>
  // `reply_to` në snake_case: ashtu e lexon serveri (`req.body?.reply_to`).
  post(`/channels/${channelId}/messages`, replyTo ? { body, reply_to: replyTo } : { body });

/* ─── Veprime mbi një mesazh ──────────────────────────────────────────────────
 * Këto pasqyrojnë Nexuma Mobile (`nexuma-mobile/src/api.js`) dhe godasin të
 * njëjtat rrugë te `backend/routes/chatRoutes.js`.
 *
 * Mbështillen me `featureError` sepse janë shtesa: nëse një instalim i serverit
 * s'e ka ende rrugën, përdoruesi merr një mesazh të kuptueshëm në vend të një
 * 404-e të papërpunuar — po ashtu si te mobile. */
function featureError(e) {
  const msg = String(e?.message || '');
  if (/404|not found/i.test(msg)) return new Error('Ky veprim nuk mbështetet nga serveri.');
  return e;
}

const guarded = (fn) => async (...args) => {
  try { return await fn(...args); } catch (e) { throw featureError(e); }
};

/** Fshin një mesazh. Serveri e kthen si `deleted` te polling-u. */
export const chatDelete = guarded((channelId, messageId) =>
  call(`/channels/${channelId}/messages/${messageId}`, { method: 'DELETE' }));

/** Shton ose heq një reagim — serveri e kthen gjendjen e re. */
export const chatReact = guarded((channelId, messageId, emoji) =>
  post(`/channels/${channelId}/messages/${messageId}/reactions`, { emoji }));

/** Ndryshon yllin (i ruajtur për mua). */
export const chatStar = guarded((channelId, messageId) =>
  post(`/channels/${channelId}/messages/${messageId}/star`));

/** Ndryshon piketimin (i dukshëm për kanalin). */
export const chatPin = guarded((channelId, messageId) =>
  post(`/channels/${channelId}/messages/${messageId}/pin`));

/**
 * Redakton mesazhin tënd. Pa afat: një gabim shtypi nuk ka orar skadimi.
 *
 * Web-i nuk e kishte fare këtë rrugë, ndonëse serveri e mbështet dhe telefoni e
 * përdor — një mesazh i shkruar gabim nga shfletuesi mund vetëm të fshihej.
 */
export const chatEdit = guarded((channelId, messageId, body) =>
  call(`/channels/${channelId}/messages/${messageId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  }));

/**
 * Çfarë mbështet ky server. 404 = asnjë nga shtesat.
 *
 * Pa këtë, një server i papërditësuar do të nxirrte veprime që kthejnë gabim
 * sapo të preken — më keq se të mos dalin fare.
 */
export async function chatCapabilities() {
  try { return (await call('/capabilities')) || {}; }
  catch { return {}; }
}

/** Mesazhet e mia me yll, nga të gjitha bisedat. */
export async function chatStarred() {
  const data = await call('/starred');
  return Array.isArray(data) ? data : [];
}

/** Kush e ka parë mesazhin dhe kur. */
export const chatInfo = guarded((channelId, messageId) =>
  call(`/channels/${channelId}/messages/${messageId}/info`));

/** Shenjimi si i lexuar s'duhet ta prishë ekranin — dështimi kalon në heshtje. */
export async function chatMarkRead(channelId) {
  try { await post(`/channels/${channelId}/read`); } catch { /* jo fatale */ }
}

/** Mesazhet e reja që nga `sinceIso`. Kthen { now, messages }. */
export async function chatPoll(sinceIso, channelId) {
  const qs = new URLSearchParams({ since: sinceIso });
  if (channelId) qs.set('channel_id', String(channelId));
  return (await call(`/poll?${qs.toString()}`)) || { now: sinceIso, messages: [] };
}

/** Kolegët, me gjendjen online. */
export async function chatPeople() {
  const data = await call('/people');
  return Array.isArray(data) ? data : [];
}

/** Hap (ose krijo) bisedën me një koleg → { id, created }. */
export const chatOpenDm = (userId) => post(`/dm/${userId}`);

/** Kanal i ri për gjithë firmën — serveri e lejon vetëm për admin / menaxher. */
export const chatCreateChannel = ({ name, purpose, icon }) =>
  post('/channels', { name, purpose, icon });

/**
 * Grup privat — e sheh vetëm kush ftohet, dhe e hap kushdo.
 *
 * Deri tani ekzistonin vetëm dy gjendje: bisedë me një person, ose kanal i
 * gjithë firmës. Një ekip prej tre vetash nuk kishte ku të fliste.
 */
export const chatCreateGroup = ({ name, members }) =>
  post('/channels', { name, private: true, members });

/** Anëtarët e një grupi. */
export async function chatMembers(channelId) {
  const data = await call(`/channels/${channelId}/members`);
  return Array.isArray(data) ? data : [];
}

export const chatAddMembers = (channelId, members) =>
  post(`/channels/${channelId}/members`, { members });

/** Dil vetë, ose hiq dikë nëse e ke hapur ti grupin. */
export const chatRemoveMember = (channelId, userId) =>
  call(`/channels/${channelId}/members/${userId}`, { method: 'DELETE' });
